/**
 * Bundle group dynamic swap ("choose your mask" without a page load).
 *
 * Progressive enhancement over the bundle-group-picker pills, which are plain
 * links to sibling bundle PDPs. This script:
 *   - prefetches a sibling's sections on hover/touch of its pill
 *   - on click, fetches the sibling's product-main + bundle-contents sections
 *     via Shopify's Section Rendering API and swaps them in place
 *   - updates the URL with pushState (back/forward re-swap, refresh/share work)
 *   - animates via the View Transitions API when available, CSS fade otherwise
 *
 * Any failure falls back to a normal navigation to the pill's href — the
 * links keep working exactly as before if this script never runs.
 *
 * Both siblings render with the same product.bundle.json template, so their
 * section IDs match and the fetched HTML slots straight into this page.
 */
(function () {
  'use strict';

  if (!document.querySelector('.bundle-group-picker')) return;

  var busy = false;
  var pushed = false;
  var prefetchCache = {}; // pathname -> Promise<sections JSON>

  // The Shopify section wrappers we swap. Found by their content rather than
  // hardcoded IDs so template renames don't break silently; the id-suffix
  // fallback covers a sibling whose section rendered empty on this page.
  function swapWrappers() {
    var wrappers = [];
    ['.custom-product-main', '.bundle-contents'].forEach(function (sel, i) {
      var el = document.querySelector(sel);
      var wrap = el && el.closest('.shopify-section');
      if (!wrap && i === 1) {
        wrap = document.querySelector('.shopify-section[id$="__bundle_contents"]');
      }
      if (wrap && wrappers.indexOf(wrap) === -1) wrappers.push(wrap);
    });
    return wrappers;
  }

  function sectionIds(wrappers) {
    return wrappers.map(function (w) { return w.id.replace(/^shopify-section-/, ''); });
  }

  function fetchSections(href) {
    var url = new URL(href, location.origin);
    var key = url.pathname;
    if (prefetchCache[key]) return prefetchCache[key];
    var ids = sectionIds(swapWrappers());
    if (!ids.length) return Promise.reject(new Error('no swappable sections'));
    url.searchParams.set('sections', ids.join(','));
    var promise = fetch(url.toString()).then(function (res) {
      if (!res.ok) throw new Error('section fetch ' + res.status);
      return res.json();
    });
    // Drop failed prefetches so a click retries instead of reusing the error.
    promise.catch(function () { delete prefetchCache[key]; });
    prefetchCache[key] = promise;
    return promise;
  }

  function applySections(sections) {
    swapWrappers().forEach(function (wrap) {
      var id = wrap.id.replace(/^shopify-section-/, '');
      if (sections[id]) wrap.outerHTML = sections[id];
    });
    // outerHTML insertion does not execute script tags, so nothing double-inits;
    // Dawn's custom elements (slider-component etc.) re-upgrade on insertion.
  }

  function updateTitle() {
    var h1 = document.querySelector('.custom-pm__title');
    if (!h1) return;
    var parts = document.title.split('–');
    var suffix = parts.length > 1 ? parts.slice(1).join('–') : null;
    document.title = suffix ? h1.textContent.trim() + ' –' + suffix : h1.textContent.trim();
  }

  function virtualPageView() {
    // Best-effort pings so flavour views still register; each pixel is
    // optional and failures are swallowed.
    try { if (window.ttq && window.ttq.page) window.ttq.page(); } catch (e) { /* noop */ }
    try { if (window.fbq) window.fbq('track', 'PageView'); } catch (e) { /* noop */ }
    try { if (window.gtag) window.gtag('event', 'page_view', { page_location: location.href }); } catch (e) { /* noop */ }
    try { if (window.dataLayer) window.dataLayer.push({ event: 'virtual_page_view', page_location: location.href }); } catch (e) { /* noop */ }
  }

  function swapTo(href, push) {
    if (busy) return;
    busy = true;
    fetchSections(href)
      .then(function (sections) {
        var doSwap = function () {
          applySections(sections);
          if (push) {
            history.pushState({ bgpSwap: true }, '', href);
            pushed = true;
          }
          updateTitle();
        };

        var animated;
        if (document.startViewTransition) {
          animated = document.startViewTransition(doSwap).finished.catch(function () {});
        } else {
          var wrappers = swapWrappers();
          wrappers.forEach(function (w) { w.classList.add('bgp-swapping'); });
          animated = new Promise(function (resolve) { setTimeout(resolve, 160); })
            .then(doSwap)
            .then(function () {
              // Fresh wrappers arrive without the class; force a fade-in frame.
              swapWrappers().forEach(function (w) {
                w.classList.add('bgp-swapping');
                requestAnimationFrame(function () {
                  requestAnimationFrame(function () { w.classList.remove('bgp-swapping'); });
                });
              });
            });
        }

        return Promise.resolve(animated).then(function () {
          virtualPageView();
          busy = false;
        });
      })
      .catch(function () {
        // Anything unexpected: behave like the plain link this replaces.
        location.assign(href);
      });
  }

  // Click: swap instead of navigating (delegated — survives its own swaps).
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a.bundle-group-picker__pill');
    if (!a) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return; // new-tab etc.
    e.preventDefault();
    swapTo(a.getAttribute('href'), true);
  });

  // Prefetch on intent (hover / touch) so the click feels instant.
  function maybePrefetch(e) {
    var a = e.target.closest && e.target.closest('a.bundle-group-picker__pill');
    if (a) fetchSections(a.getAttribute('href')).catch(function () { /* prefetch is best-effort */ });
  }
  document.addEventListener('pointerover', maybePrefetch);
  document.addEventListener('touchstart', maybePrefetch, { passive: true });

  // Back/forward between swapped flavours re-swaps instead of reloading.
  window.addEventListener('popstate', function () {
    if (!pushed) return;
    swapTo(location.pathname + location.search, false);
  });
})();
